import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Accordion from '../components/Accordion';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { FAQ_CATEGORIES, FAQS, FaqCategory } from '../data/faq';
import { usePageMeta } from '../hooks/usePageMeta';

function Faq() {
  usePageMeta({
    title: 'FAQ | EightyThreeLabs',
    description:
      'Answers to common questions about our AI automation and Microsoft Copilot consulting services.',
  });

  const [activeCategory, setActiveCategory] = useState<FaqCategory | 'all'>('all');

  const items = useMemo(() => {
    const filtered =
      activeCategory === 'all' ? FAQS : FAQS.filter(f => f.category === activeCategory);
    return filtered.map(f => ({
      id: f.id,
      question: f.question,
      answer: (
        <>
          <p>{f.answer}</p>
          {f.link && (
            <p>
              <Link to={f.link.to} className="contact-link">
                {f.link.label} &rarr;
              </Link>
            </p>
          )}
        </>
      ),
    }));
  }, [activeCategory]);

  return (
    <Layout>
      <PageHeader
        eyebrow="FAQ"
        title="Questions, answered."
        description="Everything we're asked most often about how we work, what we build, and how engagements are structured."
      />

      <section className="section section-white">
        <div className="container">
          <div className="faq-tabs reveal" role="tablist" aria-label="FAQ categories">
            <button
              type="button"
              role="tab"
              aria-selected={activeCategory === 'all'}
              className={`faq-tab${activeCategory === 'all' ? ' active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All
            </button>
            {FAQ_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={activeCategory === cat.id}
                className={`faq-tab${activeCategory === cat.id ? ' active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="faq-list reveal reveal-delay-1">
            <Accordion items={items} />
          </div>

          <div className="faq-still-have-questions reveal reveal-delay-2">
            <h3>Still have questions?</h3>
            <p>We're happy to talk it through directly.</p>
            <Link to="/contact" className="btn-primary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Faq;
